import React, { useMemo, useState } from "react";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import api from "@/lib/api";
import { useMasterList, useInvalidateMaster } from "@/hooks/useMasterData";

/**
 * Single-select dropdown bound to a master-data list.
 * - listType: e.g. "asset_category"
 * - parentId: filter children of this parent (e.g. for asset_type filtered by category)
 * - allowAdd: if true, shows "Add new ..." inline (creates pending-review value)
 * - disabled: disables the trigger
 */
export default function MasterDataSelect({
  listType,
  parentId,
  value,
  onChange,
  placeholder = "Select…",
  allowAdd = false,
  disabled = false,
  testId,
  optionFilter,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const { data: options = [], isLoading } = useMasterList(listType, { parentId });
  const invalidate = useInvalidateMaster();

  const selected = useMemo(() => options.find((o) => o.id === value), [options, value]);

  // Base list narrowed by optionFilter (display-only), but the currently-selected value
  // is always kept available even if it falls outside the filter set (never force a change).
  const base = useMemo(() => {
    let list = optionFilter ? options.filter(optionFilter) : options;
    if (value && !list.some((o) => o.id === value)) {
      const sel = options.find((o) => o.id === value);
      if (sel) list = [sel, ...list];
    }
    return list;
  }, [options, optionFilter, value]);

  const filtered = useMemo(() => {
    if (!query) return base;
    return base.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()));
  }, [base, query]);

  const exactMatch = useMemo(
    () => filtered.find((o) => o.label.toLowerCase() === query.trim().toLowerCase()),
    [filtered, query]
  );

  const handleAdd = async () => {
    const label = query.trim();
    if (!label) return;
    try {
      const { data } = await api.post(`/master-data/${listType}`, {
        list_type: listType,
        label,
        parent_id: parentId ?? null,
      });
      toast.success(`"${data.label}" added (pending review)`);
      invalidate(listType);
      onChange?.(data.id);
      setOpen(false);
      setQuery("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add value");
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal",
            !selected && "text-slate-400"
          )}
          data-testid={testId || `select-${listType}`}
        >
          <span className="truncate">
            {selected ? selected.label : placeholder}
            {selected?.review_status === "pending" && (
              <span className="ml-1.5 text-[10px] uppercase font-semibold text-amber-700">
                · pending
              </span>
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search…"
            value={query}
            onValueChange={setQuery}
            data-testid={`${testId || `select-${listType}`}-search`}
          />
          <CommandList>
            {isLoading ? (
              <div className="py-6 text-center text-xs text-slate-500">Loading…</div>
            ) : (
              <>
                <CommandEmpty>
                  {allowAdd && query.trim() ? (
                    <button
                      type="button"
                      onClick={handleAdd}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2"
                      data-testid={`${testId || `select-${listType}`}-add`}
                    >
                      <Plus className="h-4 w-4 text-[#B8935A]" />
                      <span>
                        Add <strong>"{query.trim()}"</strong> as new
                      </span>
                    </button>
                  ) : (
                    <div className="py-6 text-center text-xs text-slate-500">No results</div>
                  )}
                </CommandEmpty>
                <CommandGroup>
                  {filtered.map((o) => (
                    <CommandItem
                      key={o.id}
                      value={o.label}
                      onSelect={() => {
                        onChange?.(o.id);
                        setOpen(false);
                        setQuery("");
                      }}
                      data-testid={`${testId || `select-${listType}`}-option-${o.id}`}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          value === o.id ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <span className="flex-1">{o.label}</span>
                      {o.review_status === "pending" && (
                        <span className="ml-1.5 text-[10px] uppercase font-semibold text-amber-700">
                          pending
                        </span>
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
                {allowAdd && query.trim() && !exactMatch && (
                  <div className="border-t border-slate-200">
                    <button
                      type="button"
                      onClick={handleAdd}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2"
                      data-testid={`${testId || `select-${listType}`}-add`}
                    >
                      <Plus className="h-4 w-4 text-[#B8935A]" />
                      <span>
                        Add <strong>"{query.trim()}"</strong> as new
                      </span>
                    </button>
                  </div>
                )}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
